import Uppy, { Body, Meta, UIPlugin, UIPluginOptions, UppyFile } from '@uppy/core';

/**
 *  PDF Generator plugin
 */
// UIPlugin<Opts, M, B> -- pre-existing API drift from an @uppy major version
// bump that made UIPlugin/BasePlugin generic over all three; unrelated to
// TASK-132. Opts is plain UIPluginOptions (only opts?.id is ever read below,
// which that base type already provides via PluginOpts) rather than the
// previous Record<string, string>, which doesn't structurally satisfy
// UIPluginOptions (its `direction` field is a 'ltr' | 'rtl' union, not a
// bare string). The local `opts` field this used to redeclare is dropped --
// BasePlugin already declares `opts: Opts` as an inherited instance field.
export default class PDFPreview extends UIPlugin<UIPluginOptions, Meta, Body> {
    type: string;

    id: string;

    title: string;

    thumbnailType: string;

    previewUrl?: string;

    file?: any;

    constructor(uppy: Uppy<Meta, Body>, opts?: UIPluginOptions) {
        super(uppy, opts);
        this.type = 'modifier';
        this.id = this.opts?.id || 'PDFPreview';
        this.title = 'PDF Generator';
        this.thumbnailType = 'application/pdf';
    }

    displayPDFPreview = () => {
        if (!this.file) {
            return;
        }

        const preview = document.querySelector(`[id="uppy_${this.file?.id}"] .uppy-Dashboard-Item-preview`);

        if (preview && this.previewUrl) {
            const embed = document.createElement('embed');
            embed.setAttribute('type', 'application/pdf');
            embed.setAttribute('src', this.previewUrl);
            embed.setAttribute('width', '100%');
            embed.setAttribute('height', '350');

            preview.parentNode?.replaceChild(embed, preview);

            // Once the embed is added, we don't need to keep the file object
            this.file = null;
        }
    };

    observePreviewAdded() {
        const targetNode = document.querySelector('body');
        const config: MutationObserverInit = { childList: true, subtree: true };

        if (!targetNode) {
            return;
        }

        const observer = new MutationObserver(() => this.displayPDFPreview());
        observer.observe(targetNode, config);
    }

    requestPreview = async (file: UppyFile<Meta, Body>) => {
        try {
            const pdfBlob = new Blob([file.data], { type: this.thumbnailType });
            this.previewUrl = URL.createObjectURL(pdfBlob);
            this.file = file;
            this.observePreviewAdded();
            this.uppy.log(`[PDFPreview] Generated thumbnail for ${file.id}`);
            // 'thumbnail:generated'/'thumbnail:error' are this plugin's own
            // custom events, not part of @uppy/core's own UppyEventMap --
            // emit()/on() work with any string event name at runtime
            // (a normal, supported pattern), just not one UppyEventMap can
            // statically know about.
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (this.uppy.emit as any)('thumbnail:generated', this.uppy.getFile(file.id), this.previewUrl);
        } catch (error) {
            console.error('Error generating PDF preview:', error);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (this.uppy.emit as any)('thumbnail:error', file, error);
        }
    };

    onFileAdded = (file: UppyFile<Meta, Body>) => {
        if (
            !file.preview &&
            file.data &&
            file.type === this.thumbnailType &&
            !file.isRemote &&
            !!file.meta.showPDFPreview
        ) {
            this.requestPreview(file);
        }
    };

    install = () => {
        this.uppy.on('file-added', this.onFileAdded);
    };

    uninstall = () => {
        this.uppy.off('file-added', this.onFileAdded);
    };
}
